import { T } from '../i18n';

/**
 * La navigation, définie UNE FOIS et partagée entre l'ordinateur et le téléphone.
 *
 * Les libellés sont ceux d'un loueur de voitures, pas ceux d'un développeur :
 * on dit « Mes voitures », pas « Flotte » ; « À valider », pas « Demandes » ;
 * « Trésorerie & prévision », pas « Prévision & capacité ».
 *
 * Les regroupements suivent la façon dont l'agence pense son métier — le comptoir,
 * les voitures, les clients, l'argent, les chiffres — et non l'organisation du code.
 */
export interface Entree {
  to: string;
  label: string;
  icone: string;
  /** Capacité requise ; absente = visible par tout le monde. */
  droit?: string;
  /**
   * Masqué pour l'agent de comptoir même s'il en a le droit.
   *
   * PLUS AUCUNE ENTRÉE NE S'EN SERT, et c'est volontaire. Ce drapeau cachait
   * sept écrans — entretien, contrôle technique, assurances, amendes,
   * accidents, cautions, factures — que l'agent avait pourtant le droit de
   * voir. Le résultat : un droit accordé côté serveur, un écran invisible côté
   * comptoir, et personne pour faire le lien.
   *
   * Avant de le réutiliser : si un écran doit être réservé, c'est un DROIT
   * qu'il lui faut, pas un drapeau de rôle — un droit se retire à une personne
   * précise depuis « Utilisateurs et droits », et il se lit au même endroit
   * que la règle du serveur.
   */
  gerantsSeulement?: boolean;
  /**
   * Sous-entrées, dépliées quand on est sur le parent ou l'une d'elles.
   * L'entretien en a une par type : le patron ne cherche pas « une
   * intervention », il cherche « où en est la vidange sur mes voitures ».
   */
  sous?: Entree[];
}
export interface Groupe { titre: string; entrees: Entree[] }

const MENU = (): Groupe[] => {
  const M = T.menu;
  return [
  {
    titre: M.secAuComptoir,
    entrees: [
      { to: '/', label: M.aujourdHui, icone: '☀' },
      { to: '/disponibilites', label: M.voituresLibres, icone: '🔍' },
      { to: '/reservations/nouvelle', label: M.nouvelleLocation, icone: '＋' },
      { to: '/locations', label: M.mesLocations, icone: '📋' },
      { to: '/contrats', label: M.contrats, icone: '📄', droit: 'voir.contrats' },
      /*
       * Le planning est ouvert au comptoir.
       *
       * Il etait masque a l'agent alors que le droit `voir.planning` lui est
       * accorde depuis toujours : c'est le menu, et lui seul, qui le cachait.
       * Au comptoir, la question « et la semaine prochaine ? » se repond sur le
       * planning ; « Voitures libres » ne dit que deux dates a la fois.
       *
       * Rien de nouveau n'est expose : il montre des locations, des voitures et
       * des clients, que l'agent voit deja. Le droit reste retirable a une
       * personne precise depuis « Utilisateurs et droits ».
       */
      { to: '/planning', label: M.planning, icone: '▦', droit: 'voir.planning' },
      { to: '/corrections', label: M.aValider, icone: '⚑' },
    ],
  },
  {
    titre: M.secMesVoitures,
    entrees: [
      { to: '/flotte', label: M.mesVoitures, icone: '🚗' },
      /*
       * L'entretien, les papiers de la voiture et l'argent du dossier sont
       * ouverts au comptoir. Ils etaient masques par un drapeau de role, pas
       * par un droit : les listes exigent `voir.flotte`, que l'agent a depuis
       * toujours. Chaque entree porte desormais le droit qui la gouverne — ce
       * qui permet a Hichem d'en retirer une a quelqu'un, ce que le drapeau
       * ne permettait pas.
       */
      {
        to: '/entretien', label: M.entretien, icone: '🔧', droit: 'voir.flotte',
        sous: [
          { to: '/entretien/vidange', label: M.vidange, icone: '·', droit: 'voir.flotte' },
          { to: '/entretien/plaquettes', label: M.plaquettes, icone: '·', droit: 'voir.flotte' },
          { to: '/entretien/pneus', label: M.pneus, icone: '·', droit: 'voir.flotte' },
          { to: '/entretien/revision', label: M.revision, icone: '·', droit: 'voir.flotte' },
          { to: '/entretien/courroie', label: M.courroie, icone: '·', droit: 'voir.flotte' },
          { to: '/entretien/batterie', label: M.batterie, icone: '·', droit: 'voir.flotte' },
        ],
      },
      { to: '/controle-technique', label: M.controleTechnique, icone: '📋', droit: 'voir.flotte' },
      { to: '/assurances', label: M.assurances, icone: '🛡', droit: 'voir.flotte' },
      { to: '/amendes', label: M.amendes, icone: '⚠', droit: 'voir.flotte' },
      { to: '/sinistres', label: M.accidents, icone: '💥', droit: 'voir.flotte' },
    ],
  },
  {
    titre: M.secMesClients,
    entrees: [
      { to: '/clients', label: M.clients, icone: '👤' },
    ],
  },
  {
    titre: M.secLArgent,
    entrees: [
      { to: '/caisse', label: M.caisse, icone: '▤', droit: 'voir.caisse' },
      { to: '/dettes', label: M.dettes, icone: '⏳', droit: 'voir.caisse' },
      { to: '/depenses', label: M.depenses, icone: '🧺', droit: 'voir.caisse' },
      { to: '/caisse-extra', label: M.caisseExtra, icone: '🗃', droit: 'voir.caisse' },
      { to: '/banque', label: M.banque, icone: '🏦', droit: 'modifier.finance' },
      { to: '/cautions', label: M.cautions, icone: '🛡', droit: 'voir.contrats' },
      { to: '/factures', label: M.factures, icone: '🧾', droit: 'voir.contrats' },
    ],
  },
  {
    titre: M.secMesChiffres,
    entrees: [
      { to: '/capacite', label: M.tresoreriePrevision, icone: '◪', droit: 'voir.analytique' },
      { to: '/analytique', label: M.analyses, icone: '📈', droit: 'voir.analytique' },
      { to: '/journal', label: M.journalDActivite, icone: '☰', droit: 'voir.journal' },
      { to: '/notes', label: M.notes, icone: '🗒', droit: 'voir.notes' },
    ],
  },
  {
    titre: M.secReglages,
    entrees: [
      // Le mode d'emploi du poste : accessible a tout le monde, sans droit particulier.
      { to: '/aide', label: M.guideDuLogiciel, icone: '📖' },
      { to: '/parametres', label: M.parametres, icone: '⚙', droit: 'gerer.parametres' },
      { to: '/utilisateurs', label: M.utilisateursEtDroits, icone: '🔑', droit: 'gerer.utilisateurs' },
      // Effacer pour de bon : réservé à Hichem, et toujours tracé au journal.
      { to: '/suppression', label: M.supprimerUneFiche, icone: '🗑', droit: 'supprimer' },
    ],
  },
  ];
};

/** Ce que la personne connectée a réellement le droit de voir. */
export function menuPour(peut: (c: string) => boolean, estAgent: boolean): Groupe[] {
  const autorisee = (e: Entree) =>
    !(e.gerantsSeulement && estAgent) && (!e.droit || peut(e.droit));
  return MENU()
    .map((g) => ({
      ...g,
      entrees: g.entrees.filter(autorisee).map((e) => (
        e.sous ? { ...e, sous: e.sous.filter(autorisee) } : e
      )),
    }))
    .filter((g) => g.entrees.length > 0);
}

/**
 * Les onglets du bas sur telephone : les gestes du quotidien, plus « Plus ».
 * Tout le reste vit derrière « Plus » — mais TOUT reste atteignable.
 */
export const ongletsPour = (estAgent: boolean): Entree[] => {
  const M = T.menu;
  return estAgent
    ? [
        { to: '/', label: M.mesTaches, icone: '☀' },
        { to: '/disponibilites', label: M.libres, icone: '🔍' },
        { to: '/reservations/nouvelle', label: M.nouvelle, icone: '＋' },
        { to: '/caisse', label: M.caisse, icone: '▤' },
      { to: '/banque', label: M.banque, icone: '🏦', droit: 'modifier.finance' },
        { to: '/plus', label: M.plus, icone: '⋯' },
      ]
    : [
        { to: '/', label: M.aujourdHui, icone: '☀' },
        { to: '/disponibilites', label: M.libres, icone: '🔍' },
        { to: '/reservations/nouvelle', label: M.nouvelle, icone: '＋' },
        { to: '/caisse', label: M.caisse, icone: '▤' },
      { to: '/banque', label: M.banque, icone: '🏦', droit: 'modifier.finance' },
        { to: '/plus', label: M.plus, icone: '⋯' },
      ];
};

/**
 * Ce qu'il faut pour ouvrir un ecran donne.
 *
 * Derive du MENU lui-meme : une entree cachee dans le menu doit etre refusee
 * quand on tape son adresse directement. Sans ce garde, l'ecran s'ouvrait, son
 * chargement partait en 403, et l'utilisateur restait devant un squelette gris
 * sans un mot — il croit le logiciel casse.
 */
export function exigencePour(chemin: string): { droit?: string; gerantsSeulement?: boolean } | null {
  const toutes = MENU().flatMap((g) => g.entrees.flatMap((e) => [e, ...(e.sous ?? [])]));
  for (const e of toutes) {
    if (e.to !== chemin) continue;
    if (!e.droit && !e.gerantsSeulement) return null;
    return { droit: e.droit, gerantsSeulement: e.gerantsSeulement };
  }
  return null;
}
